import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { backend } from "../../../../backend";
import { logAxiosError } from "../../../../logging/error";
import "./newPasswordForm.css";
import {
  validatePassword,
  validateRepeatPassword,
} from "../../../../utils/validators";
import { Tooltip as ReactTooltip } from "react-tooltip";
import PasswordRulesModal from "../../../../components/PasswordRulesModal/PasswordRulesModal";

function NewPasswordForm({ setServerError }) {
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [passwordError, setPassworError] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [tokenError, setShowTokenError] = useState(false);
  const [generalError, setShowGeneralError] = useState(false);
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const { token } = useParams(); // Token passed with the link in the reset email
  const navigate = useNavigate();

  useEffect(() => {
    if (showPasswordRules) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "auto";
    }

    return () => {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "auto";
    };
  }, [showPasswordRules]);

  const onSubmitPasswordHandler = async (e) => {
    e.preventDefault();
    setShowTokenError(false);
    setShowGeneralError(false);

    if (!passwordValidator()) {
      return;
    }

    try {
      const { data } = await axios.post(
        backend("/api/users/account/reset/password"),
        {
          token,
          password,
        }
      );

      if (!data.status) {
        setShowTokenError(true);
        return;
      }

      setShowSuccessMessage(true);
      const timeout = 5000;
      setTimeout(() => {
        navigate("/logga-in");
      }, timeout);
    } catch (error) {
      logAxiosError(error);
      setServerError(error.response?.data?.msg);
      setShowGeneralError(true);
    }
  };

  const passwordValidator = () => {
    if (!password || !repeatPassword) {
      setPassworError("Alla fält måste vara ifyllda");
      return false;
    }

    const validator = validatePassword(password);
    if (validator !== true) {
      setPassworError(validator);
      return false;
    }

    const repeatValidator = validateRepeatPassword(repeatPassword, password);
    if (repeatValidator !== true) {
      setPassworError(repeatValidator);
      return false;
    }

    return true;
  };

  const togglePasswordVisibility = (mode) => {
    if (mode === "password") {
      setShowPassword(!showPassword);
    } else if (mode === "repeat") {
      setShowRepeatPassword(!showRepeatPassword);
    }
  };

  const togglePasswordModal = (e) => {
    setShowPasswordRules(!showPasswordRules);
  };

  const renderTooltipIcon = (tooltipHtml) => (
    <svg
      data-tooltip-id="my-tooltip"
      data-tooltip-html={tooltipHtml}
      viewBox="0 0 512 512"
      fill="currentColor"
      height="28px"
      width="28px"
    >
      <path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0 0 114.6 0 256s114.6 256 256 256zm-86.2-346.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1 0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6-13.3 0-24-10.7-24-24v-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1 0-8.4-6.8-15.1-15.1-15.1h-58.3c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM288 352c0 17.7-14.3 32-32 32s-32-14.3-32-32 14.3-32 32-32 32 14.3 32 32z" />
    </svg>
  );

  return (
    <div className="new-password">
      <h1>Välj ett nytt lösenord</h1>
      <form onSubmit={onSubmitPasswordHandler}>
        <div className="new-password-label-container">
          <label htmlFor="new-password-input">Lösenord</label>
          <div onClick={togglePasswordModal}>
            {renderTooltipIcon(
              "Klicka för att läsa mer om reglerna kring lösenord"
            )}
          </div>
        </div>

        <div className="new-password-input-container">
          <input
            type={showPassword ? "text" : "password"}
            id="forgot-password-input"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPassworError("");
              setShowGeneralError(false);
              setShowTokenError(false);
            }}
          />
          <img
            src={`/images/icons/eye_${showPassword ? "visible" : "hidden"}.svg`}
            alt={showPassword ? "visible" : "hidden"}
            onClick={() => togglePasswordVisibility("password")}
          />
        </div>
        <div className="new-password-label-container">
          <label
            htmlFor="forgot-password-repeat"
            className="forgot-password-input-container"
          >
            Upprepa lösenord
          </label>
        </div>
        <div className="new-password-input-container">
          <input
            type={showRepeatPassword ? "text" : "password"}
            id="forgot-password-repeat"
            value={repeatPassword}
            onChange={(e) => {
              setRepeatPassword(e.target.value);
              setPassworError("");
              setShowGeneralError(false);
              setShowTokenError(false);
            }}
          />
          <img
            src={`/images/icons/eye_${
              showRepeatPassword ? "visible" : "hidden"
            }.svg`}
            alt={showRepeatPassword ? "visible" : "hidden"}
            onClick={() => togglePasswordVisibility("repeat")}
          />
        </div>
        {passwordError && (
          <span className="forgot-password-error-msg">{passwordError}</span>
        )}
        {tokenError && (
          <span className="forgot-password-error-msg">
            Ogiltig token, vänligen be om en ny länk
          </span>
        )}
        {generalError && (
          <span className="forgot-password-error-msg">
            Sessionen har löpt ut. Vänligen begär en ny länk via Glömt lösenord.
          </span>
        )}
        <div>
          {showSuccessMessage && (
            <>
              <p id="forgot-password-confirm">Ditt lösenord har sparats</p>
              <p id="forgot-password-confirm">
                Du kommer att bli omdirigerad till logga-in sidan om 5 sekunder!
              </p>
            </>
          )}
          <button id="forgot-password-button">Spara lösenord</button>
        </div>
      </form>
      <p className="forgot-password-text">
        Läs om hur vi hanterar personlig information i våra{" "}
        <a href="/anvandarvillkor" target="_blank" rel="noopener noreferrer">
          Användarvillkor
        </a>
        .
      </p>
      {showPasswordRules && (
        <div className="modal-overlay">
          <PasswordRulesModal toggleModal={togglePasswordModal} />
        </div>
      )}
      <ReactTooltip id="my-tooltip" className="register-tooltip" />
    </div>
  );
}

export default NewPasswordForm;
